import { createSelector } from '@app/utils/createSelector'
import { catchFetchDataError } from '@app/utils/performFetchData'

import type { ApiTrainingRequest, TrainingFormatName } from '@app/packages/api/v2/types/entities'
import type { TrainingSignUpRequest } from '@app/packages/api/v2/types/params'
import { createLoadable } from '@app/packages/thunkSelector/thunkSelector'
import type { SliceState } from '@app/packages/thunkSelector/withCheckedSliceState'
import { withCheckedSliceState } from '@app/packages/thunkSelector/withCheckedSliceState'

import { createReduxSlice } from '@app/store/redux_slice'
import { profileUserResultSelector } from '@app/store/selectors/profile'
import { createThunk } from '@app/store/thunk'

import { markProfileTrainingRequestedSlice } from './profile'

const trainingRequestSlice = createReduxSlice<SliceState<ApiTrainingRequest | null>>('training_request')

/**
 * post/put return the up-to-date request; when the slice is already loaded write it straight in
 * (keeping the freshness hash) so a convert-to-school PUT — which doesn't change the user-derived
 * hash — is reflected without a refetch. When unloaded, leave it for the selector.
 */
export const setTrainingRequestSlice = (request: ApiTrainingRequest) => trainingRequestSlice.update(state => (state ? { ...state, data: request } : state))

export const createTrainingRequest = (request: TrainingSignUpRequest) =>
  createThunk(async dispatch => {
    const [{ postTrainingRequest }, { callV2Api }] = await Promise.all([
      import('@app/packages/api/v2/actions/training_request'),
      import('@app/store/actions/api'),
    ])
    const resp = await dispatch(callV2Api(postTrainingRequest(request)))
    dispatch(setTrainingRequestSlice(resp.data))
    dispatch(markProfileTrainingRequestedSlice())
    return resp
  })

export const convertTrainingRequestFormat = (id: string, format: TrainingFormatName) =>
  createThunk(async dispatch => {
    const [{ putTrainingRequestById }, { callV2Api }] = await Promise.all([
      import('@app/packages/api/v2/actions/training_request'),
      import('@app/store/actions/api'),
    ])
    const resp = await dispatch(callV2Api(putTrainingRequestById(id, { format })))
    dispatch(setTrainingRequestSlice(resp.data))
    return resp
  })

export const trainingRequestResultSelector = createSelector([trainingRequestSlice.selector, profileUserResultSelector], (sliceState, userResult) =>
  createLoadable(c => {
    const user = c.unwrap(userResult)
    if (!user || user.account_type !== 'sitter') return null
    return withCheckedSliceState(c, {
      sliceState,
      slice: trainingRequestSlice,
      hash: [user.id, user.account_type, user.has_training_request, user.training_completed, user.approved].join(':'),
      load: async dispatch => {
        const [{ getTrainingRequest }, { callV2Api }] = await Promise.all([
          import('@app/packages/api/v2/actions/training_request'),
          import('@app/store/actions/api'),
        ])
        const resp = await dispatch(callV2Api(getTrainingRequest())).catch(catchFetchDataError(dispatch, 'Training request fetch failed'))
        return resp.data
      },
    })
  })
)
